import { website } from '@getpopsure/private-constants';
import { Button, InformationBox } from '@popsure/dirty-swan';
import classNames from 'classnames';
import { EmailCheckerInput } from 'components/EmailCheckerInput';
import { GoogleLoginButton } from 'features/GoogleOAuth';
import { ChangeEvent } from 'react';
import AnimateHeight from 'react-animate-height';
import { Trans, useSafeTranslation } from 'shared/i18n';

import styles from './Email.module.scss';

type TitleProps = {
  text?: string;
};

export const Title = ({ text }: TitleProps) => {
  const { t } = useSafeTranslation();

  return (
    <h1 className="p-h1 mb8">
      {text ||
        t(
          'components.email.titleContinueToFeatherAccount',
          'Let’s continue with a Feather account'
        )}
    </h1>
  );
};

type TermsAndConditionsProps = {
  onChange: () => void;
  showTerms?: boolean;
};

export const TermsAndConditions = ({
  onChange,
  showTerms = true,
}: TermsAndConditionsProps) => {
  if (!showTerms) return <></>;

  return (
    <div className="p-label-container mt24">
      <input
        id="terms"
        className="p-checkbox"
        type="checkbox"
        data-testid="email-tnc"
        onChange={onChange}
      />
      <label
        htmlFor="terms"
        className={`p-label ${styles.label}`}
        data-cy="email-tnc"
      >
        <div className="p-p">
          <Trans i18nKey="components.email.agreement">
            I have read and understood the{' '}
            <a
              className="p-a"
              target="_blank"
              rel="noopener noreferrer"
              href={website.privacyPolicy}
            >
              privacy policy
            </a>{' '}
            and{' '}
            <a
              className="p-a"
              target="_blank"
              rel="noopener noreferrer"
              href={website.termsAndConditions}
            >
              T&Cs
            </a>
            .
          </Trans>
        </div>
      </label>
    </div>
  );
};

type SubtitleProps = {
  text?: string;
};

export const Subtitle = ({ text }: SubtitleProps) => {
  const { t } = useSafeTranslation();

  return (
    <div className="p-p">
      {text ||
        t(
          'components.email.initial',
          'We’ll create one if you’re new to Feather.'
        )}
    </div>
  );
};

type InputProps = {
  show: boolean;
  value: string;
  emailInputOnChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showTerms?: boolean;
  tAndCInputOnChange: () => void;
};

export const Input = ({
  show,
  value,
  emailInputOnChange,
  tAndCInputOnChange,
  showTerms,
}: InputProps) => {
  const { t } = useSafeTranslation();

  return (
    <AnimateHeight duration={300} height={show ? 'auto' : 0}>
      <EmailCheckerInput
        className={classNames(styles.emailInput, 'mt16 wmx6')}
        type="email"
        autoComplete="email"
        value={value}
        placeholder={t('components.email.placeholder', 'Email address')}
        onChange={emailInputOnChange}
        required
        data-cy="input-email"
      />
      <TermsAndConditions onChange={tAndCInputOnChange} showTerms={showTerms} />
    </AnimateHeight>
  );
};

export const GoogleError = () => {
  const { t } = useSafeTranslation();

  return (
    <InformationBox className="mt16 wmx6" variant="error">
      {t(
        'components.email.googleLoginError',
        "Your account isn't linked to Google yet - please log in with your email and sign-in code first, then link your Google account in Account settings."
      )}
    </InformationBox>
  );
};

type ActionsProps = {
  isEmailSet: boolean | string | true | null | undefined;
  disabled: boolean;
  setContinueWithEmail: (value: boolean) => void;
  googleOnSuccess: () => void;
  googleOnError: () => void;
  loading?: boolean | undefined;
};

export const Actions = ({
  isEmailSet,
  disabled,
  loading,
  setContinueWithEmail,
  googleOnSuccess,
  googleOnError,
}: ActionsProps) => {
  const { t } = useSafeTranslation();

  return (
    <div className={classNames(styles.buttonContainer, 'd-flex gap8 mt24')}>
      {isEmailSet ? (
        <Button
          className="wmn3"
          disabled={disabled}
          data-cy="button-continue"
          key="button-continue"
          type="submit"
          loading={loading}
        >
          {t('components.email.button', 'Continue')}
        </Button>
      ) : (
        <Button
          className="wmn3"
          data-cy="button-continue-with-email"
          key="button-continue-with-email"
          onClick={() => setContinueWithEmail(true)}
          type="button"
        >
          {t('components.email.buttonWithEmail', 'Continue with email')}
        </Button>
      )}

      <GoogleLoginButton
        onSuccess={googleOnSuccess}
        onLoginError={googleOnError}
      />
    </div>
  );
};
